import { useTranslation } from 'react-i18next';
import { Badge } from '@/components/ui/badge';
import { Minus, ArrowUpRight, ArrowDownRight } from 'lucide-react';

/**
 * Single comparison bar row: shows yours vs industry-avg as stacked horizontal
 * bars with a +/- % delta badge. Used inside ComparisonMetricsCard.
 */
export function ComparisonBar({ label, testId, yours, avg, diffPct, status, formatValue }) {
  const { t } = useTranslation();
  const maxVal = Math.max(yours, avg) * 1.1;
  const yoursPct = (yours / maxVal) * 100;
  const avgPct = (avg / maxVal) * 100;

  let DiffIcon = Minus;
  if (diffPct > 5) DiffIcon = ArrowUpRight;
  else if (diffPct < -5) DiffIcon = ArrowDownRight;

  return (
    <div className="space-y-2" data-testid={`model-reference-${testId}`}>
      <div className="flex items-center justify-between">
        <span className="text-sm font-medium text-[#171A1D]">{label}</span>
        <Badge
          variant="outline"
          className="text-xs"
          style={{ borderColor: `${status.color}40`, color: status.color }}
        >
          <DiffIcon className="w-3 h-3 mr-1" />
          {diffPct > 0 ? '+' : ''}{diffPct}%
        </Badge>
      </div>

      <div className="space-y-1">
        <div className="flex items-center gap-3">
          <span className="text-xs text-[#66707A] w-20 shrink-0">{t('benchmark.yourEstimate')}</span>
          <div className="flex-1 h-5 bg-[#F5F6F7] rounded-sm overflow-hidden relative">
            <div
              className="h-full rounded-sm transition-all duration-500"
              style={{ width: `${yoursPct}%`, backgroundColor: status.color }}
            />
          </div>
          <span
            className="text-xs font-medium text-[#171A1D] w-24 text-right shrink-0"
            style={{ fontFamily: 'IBM Plex Mono, monospace' }}
          >
            {formatValue(yours)}
          </span>
        </div>

        <div className="flex items-center gap-3">
          <span className="text-xs text-[#8A929B] w-20 shrink-0">{t('benchmark.industryAvg')}</span>
          <div className="flex-1 h-5 bg-[#F5F6F7] rounded-sm overflow-hidden relative">
            <div
              className="h-full rounded-sm bg-[#DDE1E5] transition-all duration-500"
              style={{ width: `${avgPct}%` }}
            />
          </div>
          <span
            className="text-xs text-[#8A929B] w-24 text-right shrink-0"
            style={{ fontFamily: 'IBM Plex Mono, monospace' }}
          >
            {formatValue(avg)}
          </span>
        </div>
      </div>
    </div>
  );
}
